/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { EpisodeInfo, GroupingType, SeasonsData } from 'client-code/episode-guide';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { AllEpisodesListUtility } from '../../views/ListViewScreen/AllEpisodesList/AllEpisodesListUtility';
import { EpisodeGuideError } from '../error/EpisodeGuideError';
import { VodBrowseError } from '../error/VodBrowseError';
import { NdvrInteractor, ServicesClientImpl, VodInteractor } from '../interactors';
import { Locale, LocaleKeys } from '../locale';
import { Log } from '../logging';
import { SharedPrefConstants, SharedPreferences } from '../storage';
import { TUpcomingOffersList, TVodBrowseData } from '../types';
import { lruSet } from '../utils/LruCacheUtils';

type ComputedSeasonData = {
    seasonData: SeasonsData;
    showSeasonPicker: boolean;
    pickerType: 'header' | 'picker';
    seasonGrouping: string[];
    seasonGroupingType: GroupingType;
    totalEpisodeTillSeason: number[];
};

export class ListView {
    private static computedSeasonDataMap: Map<string, ComputedSeasonData> = new Map();

    private readonly TAG: string = 'ListView';

    private mService: ServicesClientImpl;

    private mVodServices: VodInteractor;

    private ndvrInteractor: NdvrInteractor;

    public constructor() {
        this.mService = new ServicesClientImpl();
        this.mVodServices = new VodInteractor();
        this.ndvrInteractor = new NdvrInteractor();
    }

    private createEpisodeGuideService = (error: any) => {
        const episodeGuideServiceError = error?.response?.data;
        const episodeGuideError = new EpisodeGuideError(
            episodeGuideServiceError?.code,
            episodeGuideServiceError?.message,
            Locale.getString(LocaleKeys.LIST_VIEW_SCREEN_ERROR_MESSAGE),
        );
        const description = episodeGuideError
            .getDescription()
            .replace('ERROR_CODE', episodeGuideError.getCode());

        episodeGuideError.setDescription(description);
        Diagnostics.error(this.TAG, 'EpisodeGuideError', episodeGuideError);
        throw new Error(`${description}`);
    };

    private static calculateEpisodeSlicing(
        seasonsData: SeasonsData,
        allEpisodes: EpisodeInfo[],
        offset: number,
        limit: number,
    ) {
        // Group episodes by season to understand the structure
        const episodesBySeason = allEpisodes.reduce(
            (acc: { [key: number]: EpisodeInfo[] }, episode: EpisodeInfo) => {
                const season = episode.seasonOrYear || 0;
                if (!acc[season]) acc[season] = [];
                acc[season].push(episode);
                return acc;
            },
            {},
        );
        // Calculate how many episodes we need based on offset and available space for headers
        let totalItemsToSkip = offset;
        let episodesIndex = 0;

        // eslint-disable-next-line no-restricted-syntax
        for (const season of seasonsData.seasons || []) {
            const seasonKey = season.seasonOrYear || 0;
            const seasonHeaderSize = 2; // Assuming each season header takes 2 slots
            const seasonEpisodes = episodesBySeason[seasonKey] || [];
            const seasonTotalItems = seasonHeaderSize + seasonEpisodes?.length;

            if (totalItemsToSkip >= seasonTotalItems) {
                totalItemsToSkip -= seasonTotalItems;
                episodesIndex += seasonEpisodes?.length || 0;
            } else if (totalItemsToSkip >= seasonHeaderSize) {
                const episodesToSkip = totalItemsToSkip - seasonHeaderSize;
                episodesIndex += episodesToSkip;
                totalItemsToSkip = 0;
                break;
            } else {
                // We are within the header, so we don't skip any episodes
                totalItemsToSkip = 0;
                break;
            }
        }
        const remainingEpisodes = allEpisodes.slice(episodesIndex);
        const uniqueSeasonsRemaining = [
            ...new Set(
                remainingEpisodes
                    .map((ep: EpisodeInfo) => ep.seasonOrYear)
                    .filter((season): season is number => season !== undefined),
            ),
        ];
        const headersSpaceNeeded = uniqueSeasonsRemaining.length * 2;

        // Calculate how many episodes we can show
        const maxEpisodesCanShow = Math.max(0, limit - headersSpaceNeeded);
        const episodesToShow = remainingEpisodes.slice(0, maxEpisodesCanShow);

        const seasonsToShow = new Set(uniqueSeasonsRemaining);
        return {
            episodesToShow,
            seasonsToShow,
            actualEpisodesNeeded: episodesToShow.length,
        };
    }

    private async computeSeasonData(id: string): Promise<ComputedSeasonData> {
        if (ListView.computedSeasonDataMap.has(id)) {
            return ListView.computedSeasonDataMap.get(id)!;
        }

        const seasonResponse: SeasonsData = await this.ndvrInteractor.getSeasonData({
            unifiedCollectionId: id,
            includeStickyInfo: true,
            filter: 'all',
        });

        // Pre-compute total episodes till each season for quick access later
        // number at index i represents total episodes from season 0 to season i
        const totalEpisodeTillSeason: number[] = [];
        seasonResponse.seasons?.forEach((season) => {
            const prevTotal =
                totalEpisodeTillSeason.length > 0
                    ? totalEpisodeTillSeason[totalEpisodeTillSeason.length - 1]
                    : 0;
            totalEpisodeTillSeason.push(prevTotal + (season.episodeCount || 0));
        });

        const showSeasonPicker = (seasonResponse.seasons?.length ?? 0) > 1;

        // If the episode list contains 30 shows or fewer,
        // the Season Picker becomes a Season Header displaying only the header section
        // of the season picker and is not selectable or interactive.
        // ref :
        // http://w3-engr/MyWorld/UserInterface/Hydra/Specs/STB/ui_areas/content.html#season_picker
        const pickerType = seasonResponse.totalEpisodesCount <= 30 ? 'header' : 'picker';

        // Get all seasons from response and remove -1 (extra season) from it
        const hasExtraSeason = seasonResponse.seasons?.some((s) => s.seasonOrYear === -1) ?? false;
        const regularSeasons = seasonResponse.seasons?.filter((s) => s.seasonOrYear !== -1) ?? [];
        const totalSeasons = regularSeasons.length;

        let seasonGrouping: string[] = [];
        if (showSeasonPicker) {
            if (totalSeasons <= 10) {
                seasonGrouping = regularSeasons.map((season) => season.seasonOrYear.toString());
            } else {
                // eslint-disable-next-line no-lonely-if
                if (seasonResponse.grouping === GroupingType.Season) {
                    const groupSize = Math.ceil(totalSeasons / 6);
                    const roundedGroupSize = Math.ceil(groupSize / 5) * 5;
                    for (let i = 0; i < totalSeasons; i += roundedGroupSize) {
                        const startSeason = regularSeasons[i].seasonOrYear;
                        const endIndex = Math.min(i + roundedGroupSize - 1, totalSeasons - 1);
                        const endSeason = regularSeasons[endIndex].seasonOrYear;
                        // If the last group has only one season, treat it as a single season
                        if (i === endIndex && totalSeasons - i === 1) {
                            seasonGrouping.push(startSeason.toString());
                        } else {
                            seasonGrouping.push(`${startSeason}-${endSeason}`);
                        }
                    }
                } else if (seasonResponse.grouping === GroupingType.Year) {
                    const groupSize = Math.ceil(totalSeasons / 3);
                    const roundedGroupSize = Math.ceil(groupSize / 5) * 5;
                    for (let i = 0; i < totalSeasons; i += roundedGroupSize) {
                        const startYear = regularSeasons[i].seasonOrYear;
                        const endIndex = Math.min(i + roundedGroupSize - 1, totalSeasons - 1);
                        const endYear = regularSeasons[endIndex].seasonOrYear;
                        // If the last group has only one year, treat it as a single year
                        if (i === endIndex && totalSeasons - i === 1) {
                            seasonGrouping.push(startYear.toString());
                        } else {
                            const endYearFormatted = endYear.toString().slice(-2);
                            seasonGrouping.push(`${startYear}-‘${endYearFormatted}`);
                        }
                    }
                }
            }
            // Append extra season (-1) back if it exists
            if (hasExtraSeason) {
                seasonGrouping.push('-1');
            }
        }

        const responseData: ComputedSeasonData = {
            seasonData: seasonResponse,
            showSeasonPicker,
            pickerType,
            seasonGrouping,
            seasonGroupingType: seasonResponse.grouping || GroupingType.Season,
            totalEpisodeTillSeason,
        };
        lruSet(ListView.computedSeasonDataMap, id, responseData, 50);
        return responseData;
    }

    /**
     * Fetches the VodItemList for My Rentals.
     * @param id - The parent ID for the VodItemList.
     * @returns A promise that resolves to a VodBrowseData
     *  object containing the VodItemList and total count.
     */
    public async getMyRentalsVodBrowseData(
        id: string,
        includeAdult: boolean,
        offset: number = 0, // Default offset for pagination
        limit: number = 18, // Default limit for number of items to fetch (same as streamer)
    ): Promise<TVodBrowseData> {
        try {
            const response = await this.mVodServices.getVodBrowseData({
                parentVodItemId: id,
                offset,
                limit,
                includeAdult,
            });
            const { vodItemList, totalCount } = response;
            return { vodItemList, totalCount };
        } catch (error: any) {
            const vodBrowseServiceError = error?.response?.data;
            const vodBrowseError = new VodBrowseError(
                vodBrowseServiceError?.code,
                vodBrowseServiceError?.message,
                Locale.getString(LocaleKeys.LIST_VIEW_SCREEN_ERROR_MESSAGE),
            );
            const description = vodBrowseError
                .getDescription()
                .replace('ERROR_CODE', vodBrowseError.getCode());

            vodBrowseError.setDescription(description);
            Diagnostics.error(this.TAG, 'VodBrowseError', vodBrowseError);
            throw new Error(`${description}`);
        }
    }

    public async getUpcomingData(
        id: string,
        offset: number = 0,
        limit: number = 18,
    ): Promise<TUpcomingOffersList> {
        try {
            const res = await this.ndvrInteractor.getUpcomingOffersForCollectionId({
                objectId: id,
                minEndTime: new Date().toISOString(),
                offset,
                limit,
                screenType: APPLICATION_FEATURE_AREA.LIST_VIEW_SCREEN,
            });
            return res;
        } catch (error: any) {
            Log.e(this.TAG, error);
            Diagnostics.error(this.TAG, 'UpcomingOffersError', error);
            throw new Error(error.message);
        }
    }

    public async getSeasonData(
        id: string,
        partnerId?: string,
        collapsedVodItemId?: string,
        offset: number = 0,
        limit: number = 19,
    ): Promise<{ episodes: EpisodeInfo[]; seasonData: SeasonsData; totalCount: number }> {
        try {
            const seasonResponse: SeasonsData = await this.ndvrInteractor.getSeasonData({
                unifiedCollectionId: id,
                includeStickyInfo: true,
                filter: 'vodOnDemand',
                partnerId: partnerId || '',
                collapsedVodItemId: collapsedVodItemId || '',
            });
            const episodesResponse = await this.ndvrInteractor.getEpisodes({
                unifiedCollectionId: id,
                partnerId: partnerId || '',
                collapsedVodItemId: collapsedVodItemId || '',
                filter: 'vodOnDemand',
                limit: seasonResponse.totalEpisodesCount,
                offset: 0,
            });

            const { episodesToShow } = ListView.calculateEpisodeSlicing(
                seasonResponse,
                episodesResponse.episodeInfo || [],
                offset,
                limit,
            );
            return {
                episodes: episodesToShow,
                seasonData: seasonResponse,
                totalCount: seasonResponse.totalEpisodesCount,
            };
        } catch (error: any) {
            throw this.createEpisodeGuideService(error);
        }
    }

    public async getAllEpisodesData({
        unifiedCollectionId,
        sort,
        offset = 0,
        limit = AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES,
        hideAdult = false,
    }: {
        unifiedCollectionId: string;
        sort: string;
        offset?: number;
        limit?: number;
        hideAdult?: boolean;
    }): Promise<EpisodeInfo[]> {
        try {
            const computedSeasonData = await this.computeSeasonData(unifiedCollectionId);
            const seasonResponse = computedSeasonData.seasonData;
            const { totalEpisodeTillSeason } = computedSeasonData;

            // Count how many headers (season boundaries) come before the offset
            // Each season takes 1 header slot,
            // so season i ends at position: (i+1) + totalEpisodeTillSeason[i]
            let headersConsumedBeforeOffset = totalEpisodeTillSeason.reduce((acc, curr, index) => {
                const seasonEndPosition = index + 1 + curr;
                if (offset > seasonEndPosition) return acc + 1;
                return acc;
            }, 0);

            // If offset > 0, we've passed at least the initial header at position 0
            if (offset > 0) {
                headersConsumedBeforeOffset += 1;
            }

            // Count how many headers come before offset + limit
            let headersConsumedTillLimit = totalEpisodeTillSeason.reduce((acc, curr, index) => {
                const seasonEndPosition = index + 1 + curr;
                if (offset + limit > seasonEndPosition) return acc + 1;
                return acc;
            }, 0);

            // If offset + limit > 0, the range includes at least the initial header
            if (offset + limit > 0) {
                headersConsumedTillLimit += 1;
            }

            // Calculate headers in this slice
            const headersInThisSlice = headersConsumedTillLimit - headersConsumedBeforeOffset;

            const adjustedOffset = offset - headersConsumedBeforeOffset;
            const adjustedLimit = limit - headersInThisSlice;
            const episodesResponse = await this.ndvrInteractor.getEpisodes({
                unifiedCollectionId,
                filter: 'all',
                limit: adjustedLimit,
                offset: adjustedOffset,
                hideAdult,
                sort: sort as 'seasonOrYearAsc' | 'dateDesc',
            });
            const listViewScreenSharedPreferenceEditor = new SharedPreferences(
                SharedPrefConstants.LIST_SCREEN_PREF_KEY,
            ).edit();
            listViewScreenSharedPreferenceEditor.putNumber(
                SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT,
                seasonResponse.totalEpisodesCount + (seasonResponse.seasons?.length || 0),
            );
            listViewScreenSharedPreferenceEditor.apply();
            return episodesResponse.episodeInfo || [];
        } catch (error: any) {
            throw this.createEpisodeGuideService(error);
        }
    }

    // eslint-disable-next-line class-methods-use-this
    public getComputedSeasonData(id: string): ComputedSeasonData | undefined {
        return ListView.computedSeasonDataMap.get(id);
    }
}
